import { Box, Button, CardMedia, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, PageHeader, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import ActivityTypeEditCard from './ActivityTypeEditCard';
import HeadTypeEditCard from './HeadTypeEditCard';
import { deleteActivityType, deleteHeadType, getActivityTypes, getHeadTypes, resetTypeStatus, selectType, setFailedType } from './typeManagementSlice';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { SearchKeyFilter } from '../../../app/AppUtils';

export default function TypeTab() {
    const dispatch = useDispatch();

    const [openEdit, setOpenEdit] = useState(false);
    const [openNew, setOpenNew] = useState(false);
    const [modalVersion, setModalVersion] = useState("");
    const [headFilter, setHeadFilter] = useState("");
    const [activityFilter, setActivityFilter] = useState("");

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { headTypes, activityTypes, headTypeDelete, activityTypeDelete } = useSelector((state) => state.management.type);

    useEffect(() => {
        if (headTypeDelete.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetTypeStatus());
            dispatch(getHeadTypes());
        } else if (headTypeDelete.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetTypeStatus());
        };
    }, [headTypeDelete.status]);

    useEffect(() => {
        if (activityTypeDelete.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetTypeStatus());
            dispatch(getActivityTypes());
        } else if (activityTypeDelete.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetTypeStatus());
        };
    }, [activityTypeDelete.status]);

    useEffect(() => {
        if (headTypes.status !== "success")
            dispatch(getHeadTypes());
    }, [dispatch, headTypes.status]);

    useEffect(() => {
        if (activityTypes.status !== "success")
            dispatch(getActivityTypes());
    }, [dispatch, activityTypes.status]);

    const callbackModalEdit = () => {
        setOpenEdit(false);
        dispatch(setFailedType(false));
    };

    const callbackModalNew = () => {
        setOpenNew(false);
        dispatch(setFailedType(false));
    };

    const handleHeadFilterResult = (item) => {
        return SearchKeyFilter(item, ["naam"], headFilter);
    }

    const handleActivityFilterResult = (item) => {
        return SearchKeyFilter(item, ["type"], activityFilter);
    }

    const newClick = (version) => {
        setModalVersion(version)
        dispatch(selectType(null));
        setOpenNew(!openNew);
    };

    const TableFragmentHead = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const { headTypes } = useSelector((state) => state.management.type);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - headTypes.data?.filter(handleHeadFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const editClick = (headType) => {
            dispatch(selectType(headType));
            setOpenEdit(!openEdit);
            setModalVersion("Head");
        };

        const deleteClick = (headTypeId) => {
            dispatch(deleteHeadType(headTypeId));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? headTypes.data?.filter(handleHeadFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : headTypes.data?.filter(handleHeadFilterResult)).map(item =>
                        <TableRow key={item.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{item.naam ? item.naam : "-"}</TableCell>
                            <Tooltip title={item.beschrijving}>
                                <TableCell align="left" sx={{
                                    whiteSpace: "nowrap",
                                    maxWidth: "200px",
                                    width: "100px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>{item.beschrijving ? item.beschrijving : "-"}</TableCell>
                            </Tooltip>
                            <TableCell align="center">
                                {item.imageUrl ? <CardMedia component="img" sx={{ height: 50, width: 50, mx: 'auto' }} image={item.imageUrl} alt="location" /> : "-"}
                            </TableCell>
                            <TableCell align="center" padding="checkbox"><IconButton onClick={() => editClick(item)}><EditIcon color="secondary" /></IconButton></TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton={true} icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item.naam} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => deleteClick(item.id)} />
                            </TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? headTypes.data?.filter(handleHeadFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : headTypes.data?.filter(handleHeadFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={5} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[5, 10, 25,
                                {
                                    label: headTypes.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: headTypes.data?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={headTypes.data?.filter(handleHeadFilterResult).length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    const TableFragmentActivity = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const { activityTypes } = useSelector((state) => state.management.type);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - activityTypes?.filter(handleActivityFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const editClick = (headType) => {
            dispatch(selectType(headType));
            setOpenEdit(!openEdit);
            setModalVersion("Activity");
        };

        const deleteClick = (activityTypeId) => {
            dispatch(deleteActivityType(activityTypeId));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? activityTypes.data?.filter(handleActivityFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : activityTypes.data?.filter(handleActivityFilterResult)).map(item =>
                        <TableRow key={item.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{item.type ? item.type : "-"}</TableCell>
                            <Tooltip title={item.description}>
                                <TableCell align="left" sx={{
                                    whiteSpace: "nowrap",
                                    maxWidth: "200px",
                                    width: "100px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>{item.description ? item.description : "-"}</TableCell>
                            </Tooltip>
                            <TableCell align="left">{item.headType ? item.headType : "-"}</TableCell>
                            <TableCell align="center">
                                {item.imageUrl ? <CardMedia component="img" sx={{ height: 50, width: 50, mx: 'auto' }} image={item.imageUrl} alt="location" /> : "-"}
                            </TableCell>
                            <TableCell align="center" padding="checkbox"><IconButton onClick={() => editClick(item)}><EditIcon color="secondary" /></IconButton></TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton={true} icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item.type} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => deleteClick(item.id)} />
                            </TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? activityTypes.data?.filter(handleActivityFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : activityTypes.data?.filter(handleActivityFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 63 * emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>)}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[5, 10, 25,
                                {
                                    label: activityTypes.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: activityTypes.data?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={activityTypes.data?.filter(handleActivityFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <Box sx={{ display: 'flex', flex: '1 1 auto' }}>
            <Grid container spacing={1} sx={{ display: 'flex', flex: '1 1 auto', pb: 1 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ContentLoader
                        successCondition={headTypes.status === "success"}
                        errorCondition={headTypes.status === "failed"}
                        content={
                            <Stack sx={{ bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5, height: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                                    <SearchField initialValue={headFilter} onDebouncedChange={setHeadFilter} />
                                    <Typography variant="h6" sx={{ ...PageHeader, pb: 0 }}>{resourceFinal[appLanguage].words.TYPE_HEAD}</Typography>
                                    <Button variant="outlined" onClick={() => newClick("Head")} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                                <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DESCRIPTION}</TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.IMAGE}</TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableFragmentHead />
                                    </Table>
                                </TableContainer>
                            </Stack>
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <ContentLoader
                        successCondition={activityTypes.status === "success"}
                        errorCondition={activityTypes.status === "failed"}
                        content={
                            <Stack sx={{ bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5, height: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                                    <SearchField initialValue={activityFilter} onDebouncedChange={setActivityFilter} />
                                    <Typography variant="h6" sx={{ ...PageHeader, pb: 0 }}>{resourceFinal[appLanguage].words.ACTIVITY}{resourceFinal[appLanguage].words.TYPE.toLowerCase()}</Typography>
                                    <Button variant="outlined" onClick={() => newClick("Activity")} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                                <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DESCRIPTION}</TableCell>
                                                <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.TYPE_HEAD}</TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.IMAGE}</TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                                <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableFragmentActivity />
                                    </Table>
                                </TableContainer>
                            </Stack>
                        }
                    />
                </Grid>
            </Grid>

            <Modal open={openEdit} onClose={callbackModalEdit}>
                <>
                    {modalVersion === "Head" && <HeadTypeEditCard key={"Edit"} handleClose={callbackModalEdit} />}
                    {modalVersion === "Activity" && <ActivityTypeEditCard key={"Edit"} handleClose={callbackModalEdit} />}
                </>
            </Modal>

            <Modal open={openNew} onClose={callbackModalNew}>
                <>
                    {modalVersion === "Head" && <HeadTypeEditCard key={"Add"} handleClose={callbackModalNew} addNew />}
                    {modalVersion === "Activity" && <ActivityTypeEditCard key={"Add"} handleClose={callbackModalNew} addNew />}
                </>
            </Modal>
        </Box>
    );
}
